import React, { useMemo } from 'react';
import Link from './resource-link';
import { FaFacebookMessenger, FaSlack, FaWhatsapp } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';
import { SiTwilio } from 'react-icons/si';
import {
    LuArrowUpRight,
    LuBookOpen,
    LuBot,
    LuBrain,
    LuCpu,
    LuClipboardCheck,
    LuGlobe,
    LuListOrdered,
    LuPanelRightClose,
    LuPlayCircle,
    LuPlug,
    LuPlus,
    LuRefreshCw,
    LuRocket,
    LuScroll,
    LuSettings,
    LuWorkflow,
    LuWrench,
} from 'react-icons/lu';
import useResourceFlash from '../hooks/use-resource-flash';
import { panelFlashKeys } from '../model/copilot-paths';
import { Spinner } from './activity';

const CHANNELS = [
    { name: 'Web Chat', href: '/channels/web', icon: LuGlobe },
    { name: 'WhatsApp', href: '/channels/whatsapp', icon: FaWhatsapp },
    { name: 'Messenger', href: '/channels/fb', icon: FaFacebookMessenger },
    { name: 'Twilio', href: '/channels/twilio', icon: SiTwilio },
    { name: 'Slack', href: '/channels/slack', icon: FaSlack },
];

const CONFIG_CHIPS = [
    { name: 'Instructions', href: '/agent', icon: LuScroll, section: 'instructions' },
    { name: 'Memory', href: '/memory', icon: LuBrain },
    { name: 'Model', href: '/llm-settings', icon: LuCpu },
    { name: 'Settings', href: '/settings', icon: LuSettings },
];

/** Resource sections in display order. `always` sections render even when empty. */
const SECTIONS = [
    { key: 'knowledge', label: 'Knowledge', icon: LuBookOpen, listHref: '/contents', always: true },
    { key: 'playbooks', label: 'Playbooks', icon: LuListOrdered, listHref: '/playbooks', always: true },
    { key: 'workflows', label: 'Workflows', icon: LuWorkflow, listHref: '/journey' },
    { key: 'tools', label: 'Tools', icon: LuWrench, listHref: '/tools', always: true },
    { key: 'channels', label: 'Channels', always: true },
    { key: 'subAgents', label: 'Sub-agents', icon: LuBot, listHref: '/sub-agents' },
    { key: 'skills', label: 'Skills', icon: HiSparkles, listHref: '/skills' },
    { key: 'mcpServers', label: 'MCP servers', icon: LuPlug, listHref: '/mcp-servers' },
];

const MAX_CHIPS = 8;
const ACTION_VERB = { created: 'Created', updated: 'Updated', deleted: 'Deleted' };
const READY_STATUSES = new Set(['', 'ready', 'active', 'completed']);

const chipClass = highlighted => `group inline-flex max-w-full items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[13px] font-medium transition-colors
    ${highlighted
        ? 'border-gray-900 bg-white text-gray-900 shadow-[0_0_0_3px_rgba(186,230,26,0.45)]'
        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900'}`;

function SectionLabel({ children, action, flash }) {
    return (
        <div className="mb-2 flex items-center justify-between">
            <h3 className={`-mx-1.5 rounded-md px-1.5 text-[12.5px] font-medium text-gray-500 ${flash ? 'animate-copilot-flash' : ''}`}>{children}</h3>
            {action}
        </div>
    );
}

function AddButton({ to, label }) {
    return (
        <Link
            to={to}
            aria-label={label}
            title={label}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
        >
            <LuPlus className="h-4 w-4" />
        </Link>
    );
}

function ResourceChip({ to, icon: Icon, name, meta, highlighted, warning, flash }) {
    return (
        <Link to={to} title={meta ? `${name} · ${meta}` : name} className={`${chipClass(highlighted)} ${flash ? 'animate-copilot-flash' : ''}`}>
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-gray-700" />}
            <span className="truncate">{name}</span>
            {warning && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" title={warning} />}
            {highlighted && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-lime" aria-label="changed in this chat" />}
        </Link>
    );
}

/** What the copilot changed in this conversation. */
function ChangeLog({ changes }) {
    if (!changes.length) return null;
    return (
        <section className="rounded-2xl border border-gray-200 bg-gray-50/70 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                <HiSparkles className="h-3.5 w-3.5 text-gray-900 dark:text-brand-lime" /> Changed in this chat
            </p>
            <ul className="space-y-1.5">
                {changes.slice(-6).reverse().map((change, index) => (
                    <li key={`${change.kind}:${change.name}:${index}`} className="flex items-start gap-2 text-[13px] text-gray-700">
                        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${change.action === 'deleted' ? 'bg-gray-300' : 'bg-brand-lime'}`} />
                        <span className="min-w-0">
                            <span className="text-gray-500">{ACTION_VERB[change.action] || 'Changed'} {change.kind.replace(/_/g, ' ')}</span>
                            {change.name && <span className="block truncate font-medium text-gray-900">{change.name}</span>}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function PanelHeader({ title, subtitle, avatar, actions, onCollapse }) {
    return (
        <header className="flex h-[72px] shrink-0 items-center gap-3 border-b border-gray-100 px-5 py-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-900">
                {avatar}
            </span>
            <div className="min-w-0 flex-1 leading-tight">
                <p className="truncate text-[15px] font-semibold text-gray-900">{title}</p>
                <p className="mt-0.5 truncate text-[12px] text-gray-500">{subtitle}</p>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
                {actions}
                <button
                    type="button"
                    onClick={onCollapse}
                    aria-label="Hide resources"
                    title="Hide resources"
                    className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                    <LuPanelRightClose className="h-[18px] w-[18px]" />
                </button>
            </div>
        </header>
    );
}

function IconAction({ onClick, label, children, spinning }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
        >
            <span className={spinning ? 'block animate-spin' : 'block'}>{children}</span>
        </button>
    );
}

function AppResources({ appId, data, changes, flashing }) {
    const base = `/app/${appId}`;
    const changedNames = useMemo(() => {
        const bySection = {};
        changes.forEach(change => {
            if (!change.section || change.action === 'deleted') return;
            (bySection[change.section] = bySection[change.section] || new Set()).add(change.name.toLowerCase());
        });
        return bySection;
    }, [changes]);
    const isChanged = (section, name) => Boolean(changedNames[section]?.has(String(name).toLowerCase()));
    const isFlashing = (section, name) => flashing.has(name == null ? section : `${section}:${String(name).toLowerCase()}`);

    return (
        <div className="space-y-6">
            <section>
                <SectionLabel flash={isFlashing('instructions')}>Instructions</SectionLabel>
                <div className="flex flex-wrap gap-2">
                    {CONFIG_CHIPS.map(chip => (
                        <ResourceChip
                            key={chip.name}
                            to={`${base}${chip.href}`}
                            icon={chip.icon}
                            name={chip.name}
                            highlighted={chip.section ? Boolean(changedNames[chip.section]) : false}
                            flash={chip.section ? isFlashing(chip.section) : false}
                        />
                    ))}
                </div>
            </section>

            {SECTIONS.map(({ key, label, icon, listHref, always }) => {
                if (key === 'channels') {
                    return (
                        <section key={key}>
                            <SectionLabel>Channels</SectionLabel>
                            <div className="flex flex-wrap gap-2">
                                {CHANNELS.map(({ name, href, icon: Icon }, index) => (
                                    index === 0
                                        ? <ResourceChip key={name} to={`${base}${href}`} icon={Icon} name={name} />
                                        : (
                                            <Link
                                                key={name}
                                                to={`${base}${href}`}
                                                aria-label={name}
                                                title={name}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-900"
                                            >
                                                <Icon className="h-3.5 w-3.5" />
                                            </Link>
                                        )
                                ))}
                            </div>
                        </section>
                    );
                }
                const items = Array.isArray(data?.[key]) ? data[key] : [];
                if (!items.length && !always) return null;
                const extra = items.length - MAX_CHIPS;
                return (
                    <section key={key}>
                        <SectionLabel flash={isFlashing(key)}>
                            {label}
                            {items.length > 0 && <span className="ml-1.5 text-gray-400">{items.length}</span>}
                        </SectionLabel>
                        <div className="flex flex-wrap items-center gap-2">
                            {items.slice(0, MAX_CHIPS).map(item => (
                                <ResourceChip
                                    key={`${item.id}:${item.name}`}
                                    to={`${base}${item.href}`}
                                    icon={icon}
                                    name={item.name}
                                    meta={item.meta}
                                    highlighted={isChanged(key, item.name)}
                                    flash={isFlashing(key, item.name)}
                                    warning={READY_STATUSES.has(String(item.status || '').toLowerCase()) ? null : `Status: ${item.status}`}
                                />
                            ))}
                            {extra > 0 && (
                                <Link to={`${base}${listHref}`} className="px-1 text-[12.5px] font-medium text-gray-500 hover:text-gray-900">
                                    +{extra} more
                                </Link>
                            )}
                            <AddButton to={`${base}${listHref}`} label={`Manage ${label.toLowerCase()}`} />
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

function AccountApplications({ data, changes, copilotPathFor }) {
    const apps = Array.isArray(data?.applications) ? data.applications : [];
    const created = new Set(changes.filter(c => c.kind === 'application').map(c => c.name.toLowerCase()));
    if (!apps.length) {
        return <p className="text-[13px] text-gray-500">No applications yet. Ask the copilot to create one.</p>;
    }
    return (
        <section>
            <SectionLabel>Your applications <span className="ml-1.5 text-gray-400">{apps.length}</span></SectionLabel>
            <ul className="space-y-1">
                {apps.map(app => (
                    <li key={app.id} className="group flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-gray-50">
                        <Link to={copilotPathFor(app.id)} className="flex min-w-0 flex-1 items-center gap-2.5" title={`Build ${app.name} with Copilot`}>
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-[13px] font-semibold text-gray-700">
                                {app.name.charAt(0).toUpperCase()}
                            </span>
                            <span className="min-w-0 leading-tight">
                                <span className="flex items-center gap-1.5 truncate text-[13.5px] font-medium text-gray-900">
                                    {app.name}
                                    {created.has(app.name.toLowerCase()) && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-lime" />}
                                </span>
                                {app.meta && <span className="block truncate text-[12px] text-gray-500">{app.meta}</span>}
                            </span>
                        </Link>
                        <Link
                            to={`/app/${app.id}/${app.appMode === 'prompt_based' ? 'agent' : 'journey'}`}
                            aria-label={`Open ${app.name}`}
                            title="Open in console"
                            className="rounded-md p-1 text-gray-300 opacity-0 transition hover:text-gray-900 group-hover:opacity-100"
                        >
                            <LuArrowUpRight className="h-4 w-4" />
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

/**
 * The right side of the copilot page: what the copilot is working on. In an application,
 * its channels, instructions and resources (things changed in this chat are highlighted);
 * for the account, its applications.
 */
export default function ResourcePanel({ appId, appInfo, isAccountScope, resources, changes, onCollapse, onPublish, onSimulate, copilotPathFor }) {
    const { data, loading, error, refresh } = resources;
    const flashing = useResourceFlash(panelFlashKeys);
    const title = isAccountScope ? 'All applications' : (appInfo?.name || 'This agent');
    const subtitle = isAccountScope
        ? 'Pick one to build it with Copilot'
        : changes.length
            ? `${changes.length} change${changes.length === 1 ? '' : 's'} in this chat · draft`
            : 'Draft · publish to make changes live';

    return (
        <aside aria-label="Agent resources" className="flex h-full w-[340px] shrink-0 flex-col border-l border-gray-200 bg-white 2xl:w-[380px]">
            <PanelHeader
                title={title}
                subtitle={subtitle}
                avatar={isAccountScope ? <HiSparkles className="h-4 w-4" /> : title.charAt(0).toUpperCase()}
                onCollapse={onCollapse}
                actions={(
                    <>
                        <IconAction onClick={() => refresh()} label="Refresh" spinning={loading && Boolean(data)}>
                            <LuRefreshCw className="h-4 w-4" />
                        </IconAction>
                    </>
                )}
            />
            {!isAccountScope && appId && (
                <div className="flex flex-wrap gap-2 border-b border-gray-100 px-5 py-3">
                    <Link to={`/app/${appId}/evaluation`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-[13px] font-medium">
                        <LuClipboardCheck className="h-4 w-4" /> Agent Tests
                    </Link>
                    {onSimulate && (
                        <button
                            type="button"
                            onClick={onSimulate}
                            title="Try the agent in the simulator"
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] font-medium text-gray-800 transition-colors hover:border-gray-300 hover:bg-gray-50"
                        >
                            <LuPlayCircle className="h-4 w-4" /> Simulator
                        </button>
                    )}
                    {onPublish && (
                        <button
                            type="button"
                            onClick={onPublish}
                            title="Publish a new version of the agent"
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-[13px] font-medium text-white transition-colors hover:bg-primary"
                        >
                            <LuRocket className="h-4 w-4 text-brand-lime" /> Publish
                        </button>
                    )}
                </div>
            )}
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5">
                <ChangeLog changes={changes} />
                {error && (
                    <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-300">
                        {error} <button type="button" onClick={() => refresh()} className="font-semibold underline">Retry</button>
                    </p>
                )}
                {!data && loading
                    ? <div className="flex items-center gap-2 text-[13px] text-gray-500"><Spinner /> Loading resources…</div>
                    : isAccountScope
                        ? <AccountApplications data={data} changes={changes} copilotPathFor={copilotPathFor} />
                        : appId && <AppResources appId={appId} data={data} changes={changes} flashing={flashing} />}
            </div>
        </aside>
    );
}
